import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import { changeLanguage, reloadResources, use } from 'i18next';

import { ContextState, useContextStore } from './contextStore';
import {
  cropConnectApiClient,
  CropConnectCountry,
  CropConnectLanguages,
  CropConnectRoleDefinitions,
  RoleAssignmentDto,
  RoleAssignmentType,
} from '../services/cropConnectApiClient';
import {
  defaultResource,
  TranslationKeys,
} from '../i18n/locales/defaultResources';

export interface TranslationState {
  initialize: () => Promise<void>;
  getAllowedTranslationLanguages: (
    userRoles: RoleAssignmentDto[]
  ) => CropConnectLanguages[];
  modifyTranslation: (
    language: CropConnectLanguages,
    key: TranslationKeys,
    value: string
  ) => Promise<void>;
  modifyTranslationWithStringKey: (
    language: CropConnectLanguages,
    key: TranslationKeys,
    value: string
  ) => Promise<void>;
  changeLanguage: (language: CropConnectLanguages) => void;
  convertCountryToLanguage: (
    countryIsoCode: CropConnectCountry
  ) => CropConnectLanguages;
}

const getBrowserLanguage = (): CropConnectLanguages => {
  const browserLanguage = navigator.language || navigator.languages[0];
  switch (browserLanguage) {
    case 'pl':
    case 'pl-PL':
      return CropConnectLanguages.PlPL;
    case 'de':
    case 'de-DE':
      return CropConnectLanguages.DeDE;
    case 'da':
    case 'da-DK':
      return CropConnectLanguages.DaDA;
    case 'fi':
    case 'fi-FI':
      return CropConnectLanguages.FiFI;
    case 'lt':
    case 'lt-LT':
      return CropConnectLanguages.LtLT;
    case 'sk':
    case 'sk-SK':
      return CropConnectLanguages.SkSK;
    case 'sv':
    case 'sv-SE':
      return CropConnectLanguages.SvSE;
    case 'en':
    case 'en-GB':
    case 'EnUS':
      return CropConnectLanguages.EnGB;
    default:
      return CropConnectLanguages.EnGB;
  }
};

const backendOptions = {
  loadPath: '{{lng}}',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: (options: any, lng: any, payload: any, callback: any) => {
    // Prevent 404 errors for FDTM module translations (???)
    if (lng.startsWith('@cropconnect')) {
      callback(null, {
        data: {},
        status: 200,
      });
      return;
    }
    try {
      if (lng === 'dev') {
        callback(null, {
          data: defaultResource,
          status: 200,
        });
        return;
      }
      const language = lng as keyof typeof CropConnectLanguages;
      const typedLanguage = CropConnectLanguages[language];
      if (typedLanguage !== undefined) {
        cropConnectApiClient
          .getTranslationsByLanguage(typedLanguage)
          .then((r) => {
            if (r.success) {
              callback(null, {
                data: r.data?.value,
                status: 200,
              });
            } else {
              callback(null, {
                data: defaultResource,
                status: 200,
              });
            }
          })
          .catch((e) => {
            callback(e, { status: 500 });
          });
        return;
      }
    } catch (e) {
      callback(null, {
        data: defaultResource,
        status: 200,
      });
      return;
    }
    callback('unknown language', { status: 500 });
  },
};

export const initializeTranslations = async () => {
  const defaultLanguage = getBrowserLanguage();

  use(Backend)
    .use(initReactI18next)
    .init({
      backend: backendOptions,
      debug: true,
      load: 'currentOnly',
      lng: defaultLanguage,
      interpolation: {
        escapeValue: false,
      },
    })
    .then(() => {})
    .catch((error) => console.error('Failed to initialize i18next:', error));
};

const modifyTranslation = async (
  language: CropConnectLanguages,
  key: TranslationKeys,
  value: string
) => {
  try {
    const requestData = { key, language, value };
    const response =
      await cropConnectApiClient.updateTranslationValue(requestData);
    if (response.success) {
      await reloadResources();
    } else {
      console.error('Failed to update translation:', response);
    }
  } catch (error) {
    console.error('Error in modifyTranslation:', error);
    throw error;
  }
};

const modifyTranslationWithStringKey = async (
  language: CropConnectLanguages,
  key: TranslationKeys,
  value: string
) => {
  await modifyTranslation(language, key, value);
};

useContextStore.subscribe(
  (newState: ContextState, previousState: ContextState) => {
    if (newState.selectedUserLanguage !== previousState.selectedUserLanguage) {
      changeLanguage(
        CropConnectLanguages[
          newState.selectedUserLanguage as keyof typeof CropConnectLanguages
        ] || CropConnectLanguages.EnGB
      );
    }
  }
);

const addLanguageToArrayIfRoleAssigned = (
  roleAssignment: RoleAssignmentDto,
  requiredRole: CropConnectRoleDefinitions,
  language: CropConnectLanguages,
  array: CropConnectLanguages[]
) => {
  if (roleAssignment.roles?.find((r) => r == requiredRole)) {
    array.push(language);
  }
};

export const useTranslationsStore = create<TranslationState>()(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  devtools((_set, _get) => ({
    initialize: async () => {
      await initializeTranslations();
    },
    getAllowedTranslationLanguages: (userRoles: RoleAssignmentDto[]) => {
      const globalAssignment = userRoles.find(
        (a) => a.type == RoleAssignmentType.Global
      );
      if (!globalAssignment) {
        return [];
      }
      if (
        globalAssignment.roles?.find(
          (r) => r == CropConnectRoleDefinitions.TranslationAdmin_All
        )
      ) {
        return Object.values(CropConnectLanguages).filter(
          (l) => l != CropConnectLanguages.None
        ) as CropConnectLanguages[];
      }
      const availableLanguages = [] as CropConnectLanguages[];
      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_EN,
        CropConnectLanguages.EnGB,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_DE,
        CropConnectLanguages.DeDE,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_DK,
        CropConnectLanguages.DaDA,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_FI,
        CropConnectLanguages.FiFI,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_LT,
        CropConnectLanguages.LtLT,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_PL,
        CropConnectLanguages.PlPL,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_SE,
        CropConnectLanguages.SvSE,
        availableLanguages
      );

      addLanguageToArrayIfRoleAssigned(
        globalAssignment,
        CropConnectRoleDefinitions.TranslationAdmin_SK,
        CropConnectLanguages.SkSK,
        availableLanguages
      );
      return availableLanguages;
    },
    modifyTranslation: async (
      language: CropConnectLanguages,
      key: TranslationKeys,
      value: string
    ) => {
      await modifyTranslation(language, key, value);
    },
    modifyTranslationWithStringKey: async (
      language: CropConnectLanguages,
      key: TranslationKeys,
      value: string
    ) => {
      await modifyTranslationWithStringKey(language, key, value);
    },
    changeLanguage: (language: CropConnectLanguages) => {
      changeLanguage(
        CropConnectLanguages[language as keyof typeof CropConnectLanguages] ||
          CropConnectLanguages.EnGB
      );
    },
    convertCountryToLanguage: (countryIsoCode: CropConnectCountry) => {
      let language: CropConnectLanguages;

      switch (countryIsoCode) {
        case CropConnectCountry.DK:
          language = CropConnectLanguages.DaDA;
          break;
        case CropConnectCountry.DE:
          language = CropConnectLanguages.DeDE;
          break;
        case CropConnectCountry.FI:
          language = CropConnectLanguages.FiFI;
          break;
        case CropConnectCountry.LT:
          language = CropConnectLanguages.LtLT;
          break;
        case CropConnectCountry.PL:
          language = CropConnectLanguages.PlPL;
          break;
        case CropConnectCountry.SK:
          language = CropConnectLanguages.SkSK;
          break;
        case CropConnectCountry.SE:
          language = CropConnectLanguages.SvSE;
          break;
        default:
          language = CropConnectLanguages.EnGB;
          break;
      }

      return language;
    },
  }))
);
